import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save,
  CheckCircle,
  GolfCourse
} from '@mui/icons-material';

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);

export default function PlayerScoring() {
  const { tournamentId, playerId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [player, setPlayer] = useState(null);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    fetchData();
  }, [tournamentId, playerId]);

  const fetchData = async () => {
    try {
      // Fetch tournament data
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (tournamentDoc.exists()) {
        setTournament(tournamentDoc.data());
      }

      // Fetch player data
      const playerDoc = await getDoc(doc(db, 'players', playerId));
      if (playerDoc.exists()) {
        setPlayer(playerDoc.data());
      }

      // Initialize scores with existing data or empty
      const initialScores = {};
      HOLES.forEach(hole => {
        initialScores[hole] = tournamentDoc.data()?.scores?.[playerId]?.[hole] || '';
      });
      setScores(initialScores);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (hole, value) => {
    const numValue = parseInt(value) || '';
    if (numValue === '' || (numValue >= 1 && numValue <= 20)) {
      setScores(prev => ({
        ...prev,
        [hole]: numValue
      }));
    }
  };

  const calculateTotal = () => {
    return Object.values(scores).reduce((total, score) => {
      return total + (parseInt(score) || 0);
    }, 0);
  };

  const calculateNetScore = () => {
    if (!player?.handicap || tournament?.type === 'STROKEPLAY_SCRATCH') {
      return calculateTotal();
    }
    
    // Calculate net score based on handicap
    const total = calculateTotal();
    const handicap = player.handicap || 0;
    return total - Math.floor(handicap);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Update tournament scores
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      await updateDoc(tournamentRef, {
        [`scores.${playerId}`]: scores,
        [`lastUpdated.${playerId}`]: new Date()
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error saving scores:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, 2));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading tournament data...</Typography>
      </Container>
    );
  }

  if (!tournament || !player) {
    return (
      <Container>
        <Alert severity="error">
          Tournament or player not found. Please check your QR code.
        </Alert>
      </Container>
    );
  }

  const steps = ['Enter Scores', 'Review Scores', 'Submit'];

  const renderScoreEntry = () => (
    <Grid container spacing={2}>
      {HOLES.map(hole => (
        <Grid item xs={6} sm={4} md={3} key={hole}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" align="center" gutterBottom>
                Hole {hole}
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={scores[hole] || ''}
                onChange={(e) => handleScoreChange(hole, e.target.value)}
                inputProps={{ min: 1, max: 20 }}
                size="small"
                placeholder="Score"
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderScoreReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Score Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Gross Score
              </Typography>
              <Typography variant="h4">
                {calculateTotal()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {tournament.type !== 'STROKEPLAY_SCRATCH' && player.handicap && (
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Net Score
                </Typography>
                <Typography variant="h4">
                  {calculateNetScore()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Handicap
              </Typography>
              <Typography variant="h4">
                {player.handicap || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <GolfCourse sx={{ mr: 2, fontSize: 40 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              {tournament.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {player.firstName} {player.lastName}
            </Typography>
          </Box>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderScoreEntry()}
        {activeStep === 1 && renderScoreReview()}
        {activeStep === 2 && (
          <Box textAlign="center">
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Ready to Submit
            </Typography>
            <Typography color="text.secondary">
              Your scores have been recorded. Click submit to finalize.
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          
          <Box>
            {activeStep < 2 ? (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Submit Scores'}
              </Button>
            )}
          </Box>
        </Box>

        {showSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Scores saved successfully!
          </Alert>
        )}
      </Paper>
    </Container>
  );
}
